'use client'

import {
  AsYouType,
  CountryCode,
  getCountries,
  getExampleNumber,
  parsePhoneNumber,
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { getCountryByTimezone } from '@goodfellas/country/dist/timezone'

function countryCodeToFlagEmoji(countryCode: string): string {
  const cc = String(countryCode || '')
    .trim()
    .toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return '🏳️'
  const A = 0x1f1e6
  const first = cc.charCodeAt(0) - 65 + A
  const second = cc.charCodeAt(1) - 65 + A
  return String.fromCodePoint(first, second)
}

export default function CountryPhoneInput({
  onChange: setSanitizedNumber,
  phone: sanitizedNumber,
  showFormLabel = true
}: {
  onChange: (phone: string) => void
  phone: string
  showFormLabel?: boolean
}) {
  const { t } = useTranslation('avaliacao360')

  const [country, setCountry] = React.useState<CountryCode>(() => {
    const browserCountry = getCountryByTimezone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )?.countryCode
    return (
      isCountryCode(browserCountry) ? browserCountry : 'BR'
    ) as CountryCode
  })
  const [phoneNumber, setPhoneNumber] = React.useState<string>('')

  // Seed the display once from an incoming value (e.g. editing an existing
  // user). Runs only until the first non-empty value is applied, so it never
  // clobbers what the user types afterwards.
  const seededRef = React.useRef(false)
  React.useEffect(() => {
    if (seededRef.current) return
    const digits = String(sanitizedNumber ?? '').replace(/\D/g, '')
    if (!digits) return
    seededRef.current = true
    try {
      const parsed = parsePhoneNumber(`+${digits}`)
      if (parsed?.country) {
        setCountry(parsed.country as CountryCode)
        const formatter = new AsYouType({
          defaultCountry: parsed.country as CountryCode,
        })
        setPhoneNumber(formatter.input(parsed.nationalNumber || ''))
        return
      }
    } catch {
      // fall through to default formatting below
    }
    const formatter = new AsYouType({ defaultCountry: country })
    setPhoneNumber(formatter.input(digits))
  }, [sanitizedNumber, country])

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedPhone = e.clipboardData.getData('text').trim()

    try {
      // Case 1: Handle international format with + prefix
      if (pastedPhone.startsWith('+')) {
        const parsedNumber = parsePhoneNumber(pastedPhone)

        if (parsedNumber && parsedNumber.country) {
          const detectedCountry = parsedNumber.country as CountryCode
          setCountry(detectedCountry)

          const formatter = new AsYouType({ defaultCountry: detectedCountry })
          const formattedNumber = formatter.input(
            parsedNumber.nationalNumber || '',
          )

          setPhoneNumber(formattedNumber)
          setSanitizedNumber(
            sanitizePhoneNumber(formattedNumber, detectedCountry),
          )
          return
        }
      }
      // Case 2: Handle potential international format WITHOUT + prefix
      else if (/^\d+$/.test(pastedPhone) && pastedPhone.length > 8) {
        // Try parsing with a + prefix to see if it's a valid international number
        try {
          const parsedNumber = parsePhoneNumber(`+${pastedPhone}`)

          if (
            parsedNumber &&
            parsedNumber.country &&
            parsedNumber.nationalNumber
          ) {
            const detectedCountry = parsedNumber.country as CountryCode
            setCountry(detectedCountry)

            const formatter = new AsYouType({ defaultCountry: detectedCountry })
            const formattedNumber = formatter.input(parsedNumber.nationalNumber)

            setPhoneNumber(formattedNumber)
            setSanitizedNumber(
              sanitizePhoneNumber(formattedNumber, detectedCountry),
            )
            return
          }
        } catch {
          // If parsing fails, it's not a valid international number without +
          // Continue to default formatting
        }
      }

      // Fall back to default if no country detected
      const formatter = new AsYouType({ defaultCountry: country })
      const formattedNumber = formatter.input(pastedPhone)

      setPhoneNumber(formattedNumber)
      setSanitizedNumber(sanitizePhoneNumber(formattedNumber, country))
    } catch (_error) {
      // Fall back to default formatting
      const formatter = new AsYouType({ defaultCountry: country })
      const formattedNumber = formatter.input(pastedPhone)

      setPhoneNumber(formattedNumber)
      setSanitizedNumber(sanitizePhoneNumber(formattedNumber, country))
    }
  }

  const sanitizePhoneNumber = (phone: string, countryCode: CountryCode) => {
    try {
      const parsedNumber = parsePhoneNumber(phone, countryCode)
      return parsedNumber?.number?.replace(/\D/g, '') || ''
    } catch {
      return phone.replace(/\D/g, '')
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < phoneNumber.length) {
      setPhoneNumber(e.target.value)
      return
    }

    const formatter = new AsYouType({ defaultCountry: country })
    const formattedNumber = formatter.input(e.target.value)

    setPhoneNumber(formattedNumber)
    setSanitizedNumber(sanitizePhoneNumber(formattedNumber, country))
  }

  return (
    <div className="relative z-0 w-full mb-2 group">
      {showFormLabel && <label
        htmlFor="userPhoneDisplay"
        className="block mb-2 text-xs uppercase text-white/70"
      >
        <strong>{t('identificatorBox.formLabels.whatsApp')}</strong>
      </label>}
      <div className="flex gap-2">
        <div className="relative">
          <span
            className="emojiFlag"
            style={{
              fontSize: '1.65rem',
              lineHeight: '1.65rem',
              marginRight: '0.75rem',
              position: 'absolute',
              left: '0.9rem',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
            aria-label={country}
          >
            {countryCodeToFlagEmoji(country)}
          </span>
          <select
            className="w-fit text-gray-900 border border-gray-300 rounded-full bg-gray-50 text-base focus:ring-red-500 focus:border-red-500 pl-12 pr-10 h-14 appearance-none"
            value={country}
            onChange={(e) => setCountry(e.target.value as CountryCode)}
          >
            {getCountries().map((c) => (
              <option key={c} value={c} className="text-gray-900">
                {c} (+{getExampleNumber(c, examples)?.countryCallingCode})
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          </div>
        </div>
        <div className="flex-1 relative">
          <input
            type="tel"
            id="userPhoneDisplay"
            placeholder={
              getExampleNumber(country, examples)?.formatNational() ?? ''
            }
            className="w-full text-gray-900 border border-gray-300 rounded-full bg-gray-50 text-base focus:ring-red-500 focus:border-red-500 p-4 h-14"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onPaste={handlePhonePaste}
          />
          <input
            type="hidden"
            name="userPhone"
            id="userPhone"
            value={sanitizedNumber}
          />
        </div>
      </div>
    </div>
  )
}

function isCountryCode(value: unknown): value is CountryCode {
  if (typeof value !== 'string') {
    return false
  }
  return getCountries().includes(value as CountryCode)
}
